import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBookOpen, 
  FiClock, 
  FiStar, 
  FiCheckCircle, 
  FiAward,
  FiTarget
} from 'react-icons/fi';
import axios from 'axios';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const LearningStats = () => {
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    totalHours: 0,
    averageGrade: 0,
    weeklyProgress: [],
    monthlyProgress: [],
    achievements: [],
    learningStreak: 0
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // week, month, year

  const fetchLearningStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/learning/stats?range=${timeRange}`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching learning stats:', error);
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchLearningStats();
  }, [fetchLearningStats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">학습 통계를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = stats.totalCourses > 0 ? (stats.completedCourses / stats.totalCourses) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">학습 통계</h1>
              <p className="text-gray-600 mt-1">학습 진행 상황과 성과를 확인하세요</p>
            </div>
            <Link
              to="/student-dashboard"
              className="bg-primary-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
            >
              대시보드로 돌아가기
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Time Range Selector */}
        <div className="mb-8">
          <div className="flex space-x-2">
            {['week', 'month', 'year'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range === 'week' ? '주간' : range === 'month' ? '월간' : '연간'}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FiBookOpen className="h-6 w-6 text-primary-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 강의 수</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <FiCheckCircle className="h-6 w-6 text-green-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">완료한 강의</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiClock className="h-6 w-6 text-blue-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 학습 시간</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalHours}시간</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <FiStar className="h-6 w-6 text-yellow-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">평균 평점</p>
                <p className="text-2xl font-bold text-gray-900">{stats.averageGrade}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Progress Chart */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">학습 진행률</h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">전체 진행률</span>
                  <span className="text-sm font-bold text-gray-900">{progressPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-primary-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">{stats.completedCourses}</div>
                  <div className="text-sm text-gray-600">완료</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-400">{stats.totalCourses - stats.completedCourses}</div>
                  <div className="text-sm text-gray-600">진행 중</div>
                </div>
              </div>
            </div>
          </div>

          {/* Learning Streak */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">학습 연속일</h2>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">{stats.learningStreak}</div>
              <div className="text-gray-600 mb-4">일 연속 학습</div>
              <div className="flex justify-center">
                <FiTarget className="h-8 w-8 text-primary-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">학습 성과</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.achievements.length > 0 ? (
                stats.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <FiAward className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{achievement.title}</p>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-gray-600">
                  <FiAward className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>아직 획득한 성과가 없습니다.</p>
                  <p className="text-sm">계속 학습하여 성과를 획득해보세요!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningStats;
